import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import BackLink from "@/components/BackLink";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Pencil, Save, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { inr, formatDate, amountInWords, GST_OPTIONS } from "@/lib/utils2";
import EmailDialog from "@/components/EmailDialog";

export default function BillDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [bill, setBill] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [company, setCompany] = useState({});
  const [defaultBank, setDefaultBank] = useState(null);
  const [party, setParty] = useState(null);

  const load = async () => {
    const [billRes, settingsRes, banksRes, partiesRes] = await Promise.all([
      api.get(`/bills/${id}`),
      api.get("/settings"),
      api.get("/bank-accounts"),
      api.get("/parties"),
    ]);
    const data = billRes.data;
    setBill(data);
    setCompany(settingsRes.data || {});
    setDefaultBank(banksRes.data.find((b) => b.is_default) || banksRes.data[0] || null);
    setParty((partiesRes.data || []).find((p) => p.id === data.party_id) || null);
    setDraft({
      bill_number: data.bill_number,
      bill_date: data.bill_date,
      gst_percent: data.gst_percent,
      notes: data.notes || "",
      lines: data.lines.map((l) => ({ ...l })),
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!bill || !draft) return <div className="p-10 text-brand-muted">Loading…</div>;

  const saveEdits = async () => {
    try {
      const payload = {
        bill_number: draft.bill_number,
        bill_date: draft.bill_date,
        bill_type: bill.bill_type,
        party_id: bill.party_id,
        gst_percent: Number(draft.gst_percent),
        truck_ids: draft.lines.map((l) => l.truck_id),
        line_edits: draft.lines.map((l) => ({
          truck_id: l.truck_id,
          weight: l.weight,
          bill_rate: l.rate,
          description: l.description || null,
        })),
        notes: draft.notes,
      };
      await api.put(`/bills/${id}`, payload);
      toast.success("Bill updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeBill = async () => {
    if (!confirm("Delete this bill? Trucks will become unbilled again.")) return;
    await api.delete(`/bills/${id}`);
    toast.success("Bill deleted");
    nav("/bills");
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/bills/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bill-${bill.bill_number.replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  };

  const setLine = (i, k, v) => {
    const lines = [...draft.lines];
    lines[i] = { ...lines[i], [k]: v };
    setDraft({ ...draft, lines });
  };

  const view = editing ? draft : bill;
  // Recompute subtotal client-side when editing
  let subtotal = 0;
  if (editing) {
    subtotal = draft.lines.reduce((s, l) => s + (Number(l.weight) || 0) * (Number(l.rate) || 0), 0);
  } else subtotal = bill.subtotal;
  subtotal = +subtotal.toFixed(2);
  const half = (Number(view.gst_percent) || 0) / 2;
  const cgst = +(subtotal * half / 100).toFixed(2);
  const sgst = +(subtotal * half / 100).toFixed(2);
  const totalPre = subtotal + cgst + sgst;
  const total = Math.round(totalPre);
  const roundOff = +(total - totalPre).toFixed(2);

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1100px] mx-auto" data-testid="bill-detail-page">
      <div className="no-print">
        <div className="flex items-center justify-between mb-6">
          <BackLink to="/bills" label="Back" testId="bill-detail-back" />
          <div className="flex gap-2">
            {!editing ? (
              <>
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-bill-btn" className="rounded-sm"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>
                <Button variant="outline" onClick={removeBill} className="rounded-sm text-brand-terracotta" data-admin-only="true"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete</Button>
                <EmailDialog
                  endpoint={`/bills/${id}/email`}
                  defaultTo={party?.email || ""}
                  defaultSubject={(() => {
                    const t = bill.bill_type;
                    const label = t === "combined" ? "Combined Tax Invoice"
                      : t === "transport" ? "Transport Bill"
                      : "Material Tax Invoice";
                    return `${label} ${bill.bill_number} — ${company.company_name || ""}`;
                  })()}
                  defaultMessage={(() => {
                    const t = bill.bill_type;
                    const label = t === "combined" ? "combined tax invoice"
                      : t === "transport" ? "transport bill"
                      : "material tax invoice";
                    const amount = Number(bill.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return `Dear ${party?.name || bill.party_name || "Sir/Madam"},\n\n` +
                      `Please find attached ${label} for your reference:\n\n` +
                      `   • Bill Number : ${bill.bill_number}\n` +
                      `   • Bill Date   : ${formatDate(bill.bill_date)}\n` +
                      `   • Amount      : ₹ ${amount}\n\n` +
                      `The invoice PDF is attached to this email.\n\n` +
                      `Kindly acknowledge receipt.\n\n` +
                      `Regards,\n${company.company_name || ""}`;
                  })()}
                  testIdPrefix="email-bill"
                  buttonLabel="Email Bill"
                />
                <Button variant="outline" onClick={downloadPdf} data-testid="download-bill-pdf-btn" className="rounded-sm"><FileDown className="h-3.5 w-3.5 mr-1.5" /> Download PDF</Button>
                <Button onClick={() => window.print()} data-testid="print-bill-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white"><Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Save PDF</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); load(); }} className="rounded-sm">Cancel</Button>
                <Button onClick={saveEdits} data-testid="save-edit-bill-btn" className="rounded-sm bg-brand-forest text-white"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button>
              </>
            )}
          </div>
        </div>

        {editing && (
          <Card className="rounded-sm border-brand-line shadow-none p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Bill Number</Label>
                <Input value={draft.bill_number} onChange={(e) => setDraft({ ...draft, bill_number: e.target.value })}
                  className="mt-1.5 h-10 rounded-sm bg-white font-mono" data-testid="edit-bill-number" />
              </div>
              <div>
                <Label>Bill Date</Label>
                <Input type="date" value={draft.bill_date} onChange={(e) => setDraft({ ...draft, bill_date: e.target.value })}
                  className="mt-1.5 h-10 rounded-sm bg-white" />
              </div>
              <div>
                <Label>GST %</Label>
                <Select value={String(draft.gst_percent)} onValueChange={(v) => setDraft({ ...draft, gst_percent: Number(v) })}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1.5 h-10 rounded-sm bg-white" />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* PDF / Print area */}
      <div className="print-area bg-white border border-brand-line rounded-sm">
        <div className="px-8 pt-7 pb-5 border-b-2 border-brand-ink">
          <div className="text-center text-[11px] uppercase tracking-[0.3em] text-brand-muted mb-2">Tax Invoice</div>
          <div className="flex justify-between items-start gap-6">
            <div className="flex items-start gap-4">
              {company.logo_base64 ? (
                <img src={company.logo_base64} alt="Logo" className="h-16 w-16 object-contain border border-brand-line rounded-sm bg-white" />
              ) : null}
              <div>
                <div className="font-serif text-3xl text-brand-ink tracking-tight leading-tight">{company.company_name || "TULSI TRADING AND LOGISTICS"}</div>
                <div className="text-xs text-brand-muted mt-1.5 leading-relaxed">
                  {company.address}<br/>{company.city}<br/>
                  Email: {company.email}
                </div>
              </div>
            </div>
            <div className="text-xs text-right space-y-0.5">
              <div><span className="text-brand-muted">GSTIN/UIN:</span> <span className="font-mono font-medium">{company.gstin}</span></div>
              <div><span className="text-brand-muted">PAN:</span> <span className="font-mono font-medium">{company.pan}</span></div>
              <div><span className="text-brand-muted">State:</span> {company.state}, Code: {company.state_code}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-4 grid grid-cols-2 gap-6 border-b border-brand-line text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">Billed To</div>
            <div className="font-serif text-lg text-brand-ink mt-1">{bill.party?.name}</div>
            {bill.party?.address && <div className="text-xs text-brand-muted">{bill.party.address}</div>}
            {bill.party?.gstin && <div className="text-xs mt-0.5"><span className="text-brand-muted">GSTIN:</span> <span className="font-mono">{bill.party.gstin}</span></div>}
            {bill.party?.pan && <div className="text-xs"><span className="text-brand-muted">PAN:</span> <span className="font-mono">{bill.party.pan}</span></div>}
            {bill.party?.state && <div className="text-xs"><span className="text-brand-muted">State:</span> {bill.party.state} · Code: {bill.party.state_code}</div>}
          </div>
          <div className="text-right text-xs space-y-1">
            <div><span className="text-brand-muted uppercase tracking-[0.15em] text-[10px]">Invoice No.</span> {editing
              ? <Input data-testid="edit-bill-number" value={draft.bill_number} onChange={(e) => setDraft({ ...draft, bill_number: e.target.value })} className="h-8 rounded-sm bg-white font-mono text-sm mt-1 ml-auto w-52 text-right" />
              : <div className="font-mono text-lg text-brand-ink font-semibold">{view.bill_number}</div>}</div>
            <div><span className="text-brand-muted uppercase tracking-[0.15em] text-[10px]">Bill Date</span> <div className="font-medium">{formatDate(view.bill_date)}</div></div>
            <div className="pt-1"><span className="text-brand-muted uppercase tracking-[0.15em] text-[10px]">Type</span> <div className="uppercase font-medium tracking-wider">{bill.bill_type} bill</div></div>
          </div>
        </div>

        <div className="px-8 py-5">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-brand-ink text-[10px] uppercase tracking-[0.12em]">
                <th className="text-left py-2 w-8">#</th>
                <th className="text-left py-2">Description / Truck</th>
                <th className="text-left py-2">HSN/SAC</th>
                <th className="text-left py-2">Unloading Date</th>
                <th className="text-left py-2">PO No.</th>
                <th className="text-left py-2">PO Date</th>
                <th className="text-right py-2">Wgt (MT)</th>
                <th className="text-right py-2">Rate</th>
                <th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {view.lines.map((l, i) => {
                const truck = bill.lines.find((x) => x.truck_id === l.truck_id)?.truck;
                const material = bill.lines.find((x) => x.truck_id === l.truck_id)?.material;
                const desc = l.description || (material?.name ? `${material.name} — ` : "") + `${truck?.vehicle_number} · ${formatDate(truck?.entry_date)}`;
                return (
                  <tr key={l.truck_id} className="border-b border-brand-line align-top">
                    <td className="py-2.5 tabular-nums">{i + 1}</td>
                    <td className="py-2.5">
                      {editing ? (
                        <Input value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)}
                          placeholder={desc} className="h-8 rounded-sm bg-white text-xs" />
                      ) : (<span>{desc}</span>)}
                    </td>
                    <td className="py-2.5 font-mono">{material?.hsn || "—"}</td>
                    <td className="py-2.5">{truck?.unloading_date ? formatDate(truck.unloading_date) : "—"}</td>
                    <td className="py-2.5">{truck?.po_number || "—"}</td>
                    <td className="py-2.5">{truck?.po_date ? formatDate(truck.po_date) : "—"}</td>
                    <td className="py-2.5 text-right tabular-nums">
                      {editing ? <Input type="number" step="0.001" value={l.weight} onChange={(e) => setLine(i, "weight", parseFloat(e.target.value) || 0)} className="h-8 w-20 rounded-sm text-right tabular-nums bg-white text-xs ml-auto" /> : l.weight}
                    </td>
                    <td className="py-2.5 text-right tabular-nums">
                      {editing ? <Input type="number" step="0.01" value={l.rate} onChange={(e) => setLine(i, "rate", parseFloat(e.target.value) || 0)} className="h-8 w-20 rounded-sm text-right tabular-nums bg-white text-xs ml-auto" /> : l.rate}
                    </td>
                    <td className="py-2.5 text-right tabular-nums font-medium">{inr((l.weight || 0) * (l.rate || 0))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="text-xs">
              <tr><td colSpan={8} className="text-right py-2 text-brand-muted uppercase tracking-[0.1em]">Taxable Value</td><td className="text-right py-2 tabular-nums font-medium">{inr(subtotal)}</td></tr>
              {view.gst_percent > 0 && (
                <>
                  <tr><td colSpan={8} className="text-right py-1.5 text-brand-muted">CGST @ {(view.gst_percent / 2)}%</td><td className="text-right py-1.5 tabular-nums">{inr(cgst)}</td></tr>
                  <tr><td colSpan={8} className="text-right py-1.5 text-brand-muted">SGST @ {(view.gst_percent / 2)}%</td><td className="text-right py-1.5 tabular-nums">{inr(sgst)}</td></tr>
                </>
              )}
              {roundOff !== 0 && <tr><td colSpan={8} className="text-right py-1.5 text-brand-muted">Round Off</td><td className="text-right py-1.5 tabular-nums">{inr(roundOff)}</td></tr>}
              <tr className="border-t-2 border-brand-ink"><td colSpan={8} className="text-right py-2.5 font-semibold uppercase tracking-[0.1em]">Total</td><td className="text-right py-2.5 tabular-nums font-serif text-lg">{inr(total)}</td></tr>
            </tfoot>
          </table>

          <div className="mt-5 text-xs">
            <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px]">Amount (in words)</div>
            <div className="mt-0.5 font-medium text-brand-ink uppercase">{amountInWords(total)}</div>
          </div>

          {bill.bill_type === "material" && view.gst_percent > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-6 text-[11px] border-t border-brand-line pt-3">
              <div>
                <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Tax Summary</div>
                <table className="w-full">
                  <thead><tr className="border-b border-brand-line"><th className="text-left py-1">HSN</th><th className="text-right py-1">Taxable</th><th className="text-right py-1">CGST</th><th className="text-right py-1">SGST</th></tr></thead>
                  <tbody><tr><td className="py-1 font-mono">{bill.lines[0]?.material?.hsn || "—"}</td><td className="py-1 text-right tabular-nums">{inr(subtotal)}</td><td className="py-1 text-right tabular-nums">{inr(cgst)}</td><td className="py-1 text-right tabular-nums">{inr(sgst)}</td></tr></tbody>
                </table>
              </div>
              <div>
                <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Tax Amount in words</div>
                <div className="font-medium uppercase">{amountInWords(cgst + sgst)}</div>
              </div>
            </div>
          )}

          {view.notes && <div className="mt-4 text-xs border-t border-brand-line pt-3"><span className="text-brand-muted uppercase tracking-[0.15em] text-[10px]">Notes:</span> {view.notes}</div>}

          <div className="mt-8 grid grid-cols-2 gap-6 text-[11px]">
            <div>
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Declaration</div>
              <div className="text-brand-muted leading-relaxed">We declare that this invoice shows the actual price of the goods/services described and that all particulars are true and correct.</div>
              {defaultBank && (
                <div className="mt-3">
                  <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Bank Details</div>
                  <div className="leading-relaxed">
                    <div><span className="text-brand-muted">Bank:</span> {defaultBank.bank_name}</div>
                    <div><span className="text-brand-muted">A/C:</span> <span className="font-mono">{defaultBank.account_number}</span></div>
                    {defaultBank.ifsc && <div><span className="text-brand-muted">IFSC:</span> <span className="font-mono">{defaultBank.ifsc}</span></div>}
                    {defaultBank.branch && <div><span className="text-brand-muted">Branch:</span> {defaultBank.branch}</div>}
                  </div>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">For {company.company_name || "TULSI TRADING AND LOGISTICS"}</div>
              {company.stamp_base64 ? (
                <img src={company.stamp_base64} alt="Stamp" className="h-24 w-24 object-contain ml-auto" data-testid="bill-stamp-img" />
              ) : (
                <div className="h-16"></div>
              )}
              <div className="border-t border-brand-ink pt-1.5 inline-block min-w-[180px]">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>

      <div className="no-print mt-6 p-5 bg-secondary/60 rounded-sm border border-brand-line text-sm">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-3">Payment Status</div>
        <div className="grid grid-cols-3 gap-4">
          <div><div className="text-xs text-brand-muted">Invoice Total</div><div className="font-serif text-2xl tabular-nums">{inr(bill.total_amount)}</div></div>
          <div><div className="text-xs text-brand-muted">Received</div><div className="font-serif text-2xl tabular-nums text-brand-forest">{inr(bill.received_total)}</div></div>
          <div><div className="text-xs text-brand-muted">Balance Due</div><div className={`font-serif text-2xl tabular-nums ${bill.balance_due > 0 ? "text-brand-terracotta" : "text-brand-forest"}`}>{inr(bill.balance_due)}</div></div>
        </div>
      </div>
    </div>
  );
}
